/**
 * @fileoverview O campo "Agente" do bloco "Agente IA" (bloco 3b dos planos).
 * Escolhe o agente que atende, na lista leve que quem vê o fluxo já enxerga.
 * "Editar agente" abre o "Gerenciar Agentes" nele (com ai_agents.view; sem
 * ai_agents.manage, só para ver). "Criar agente" abre um agente novo, que já
 * fica escolhido no bloco. No bloco com o texto dentro (o "Personalizado"),
 * "Transformar em agente" leva o texto e as fontes para um agente novo
 * (ai_agents.manage, além de editar o fluxo).
 */

import React, { useState } from "react";
import { MenuItem, Tooltip } from "@material-ui/core";
import { Eye, Info, Pencil, Plus, Sparkles } from "lucide-react";
import { toast } from "react-toastify";

import ModernSelect from "../../../../components/ModernSelect";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import AgentAvatar from "../../components/AgentManager/AgentAvatar";

const CUSTOM = "custom";

// "Editar agente" (sem ai_agents.manage, "Ver agente"): o "Gerenciar Agentes" aberto no agente.
export const AgentOpenButton = ({ classes, canManageAgents, onClick }) => (
  <button type="button" className={classes.agentAction} onClick={onClick}>
    {canManageAgents ? <Pencil size={12} /> : <Eye size={12} />}
    {canManageAgents ? "Editar agente" : "Ver agente"}
  </button>
);

const AgentName = ({ agent }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
    <AgentAvatar name={agent.name} profilePicUrl={agent.profilePicUrl} size="small" />
    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.name}</span>
  </span>
);

const AgentPicker = ({
  classes,
  W,
  nodeData,
  mode,
  agents,
  hasKnowledgeTools,
  canViewAgents,
  canManageAgents,
  dirty,
  onChoose,
  onOpenManager,
  onConvert,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  const agentId = mode === "agent" ? Number(nodeData.agentId) : null;
  const agent = agentId && agents ? agents.find((item) => item.id === agentId) || null : null;
  // O "Personalizado" só aparece para o bloco que tem o texto dentro (também depois
  // de trocar por um agente: dá para voltar).
  const hasPrompt = typeof nodeData.prompt === "string";
  const value = mode === "agent" ? String(agentId) : mode === "custom" ? CUSTOM : "";
  const muted = { color: W(0.45) };

  const renderValue = (selected) => {
    if (selected === CUSTOM) return "Personalizado (só neste fluxo)";
    if (!selected) return <span style={muted}>Escolha o agente</span>;
    if (!agents) return <span style={muted}>Carregando…</span>;
    return agent ? <AgentName agent={agent} /> : <span style={muted}>Agente não encontrado</span>;
  };

  const convert = () => {
    setConverting(true);
    onConvert().then((created) => {
      setConverting(false);
      setConfirmOpen(false);
      if (created) toast.success(`Pronto: o agente "${created.name}" já atende neste bloco.`);
    });
  };

  return (
    <div className={classes.configField}>
      <div className={classes.configLabelRow}>
        <span className={classes.configLabel}>Agente</span>
        <Tooltip
          title="Quem atende neste bloco. O texto, as fontes e o jeito de responder ficam no agente: mudar o agente muda todos os fluxos que o usam."
          placement="top"
          classes={{ tooltip: classes.configInfoTooltip }}
        >
          <span className={classes.configInfoIcon}>
            <Info size={13} />
          </span>
        </Tooltip>
      </div>

      <ModernSelect
        className={classes.agentSelect}
        size="small"
        fullWidth
        displayEmpty
        value={value}
        renderValue={renderValue}
        onChange={(event) => {
          const next = event.target.value;
          if (next === CUSTOM) onChoose(null);
          else if (next) onChoose(Number(next));
        }}
        inputProps={{ "aria-label": "Agente" }}
      >
        {!agents && (
          <MenuItem value="__loading" disabled>
            Carregando…
          </MenuItem>
        )}
        {agents && agents.length === 0 && (
          <MenuItem value="__none" disabled>
            Nenhum agente ainda
          </MenuItem>
        )}
        {(agents || []).map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            <AgentName agent={item} />
          </MenuItem>
        ))}
        {mode === "agent" && !agent && <MenuItem value={value} style={{ display: "none" }} />}
        {hasPrompt && <MenuItem value={CUSTOM}>Personalizado (só neste fluxo)</MenuItem>}
      </ModernSelect>

      {mode === "agent" && (
        <div className={classes.agentRow}>
          <span className={classes.agentHint}>O texto, as fontes e o jeito de responder vêm do agente.</span>
          {canViewAgents && agent && <AgentOpenButton classes={classes} canManageAgents={canManageAgents} onClick={() => onOpenManager(agent.id)} />}
        </div>
      )}
      {mode === "agent" && agents && !agent && <div className={classes.agentWarn}>O agente deste bloco não existe mais. Escolha outro.</div>}
      {mode === "agent" && hasKnowledgeTools && (
        <div className={classes.agentNote}>Os arquivos, casos, pesquisa e loja ligados a este bloco ficam sem uso: com agente, valem as fontes dele.</div>
      )}

      {mode === "empty" && (
        <div className={classes.agentRow}>
          <span className={classes.agentHint}>
            {agents && agents.length === 0 ? "Ainda não há agentes. Crie o primeiro para este bloco." : "Escolha quem vai atender neste fluxo."}
          </span>
          {canManageAgents && (
            <button type="button" className={classes.agentAction} onClick={() => onOpenManager(null)}>
              <Plus size={12} />
              Criar agente
            </button>
          )}
        </div>
      )}

      {mode === "custom" && (
        <div className={classes.agentNote}>
          O texto e as fontes estão neste bloco e só valem neste fluxo. Como agente, dá para usar em outros fluxos e editar num lugar só.
          {canManageAgents && (
            <div style={{ marginTop: 10 }}>
              <button type="button" className={classes.agentAction} onClick={() => setConfirmOpen(true)}>
                <Sparkles size={12} />
                Transformar em agente
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        title="Transformar em agente?"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={convert}
        confirmLoading={converting}
        confirmLabel="Transformar"
      >
        {"O texto, as fontes e o jeito de responder deste bloco vão para um agente novo, em \"Gerenciar Agentes\". " +
          "Os blocos de arquivos, casos, pesquisa e loja saem do fluxo, porque passam a ser do agente. A IA continua respondendo igual." +
          (dirty ? " Antes, o fluxo é salvo." : "")}
      </ConfirmationModal>
    </div>
  );
};

export default AgentPicker;
